// Client-side TTL cache with an SWR (stale-while-revalidate) helper.
//
// Design: the scorecard + home dashboard should paint from localStorage on
// mount so the user sees data instantly, then background-refresh from the
// server. Every read is best-effort — corrupt JSON, quota-exceeded writes,
// and a disabled localStorage all fall through gracefully to a normal
// server fetch.
//
// Not a general-purpose cache library — kept intentionally minimal so it's
// easy to audit the write-through / offline paths against real behavior.

const NS = 'tf:cache:'

// Read a cached entry. Returns { value, stale, expired, storedAt } or null.
// value is whatever was stored; the caller decides whether to paint stale
// data or wait for a fresh fetch (SWR pattern below defaults to painting
// stale immediately).
export function readCache(key) {
  try {
    const raw = localStorage.getItem(NS + key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('value' in parsed)) return null
    const storedAt = Number(parsed.storedAt) || 0
    const ttl = Number(parsed.ttl) || 0
    const now = Date.now()
    const age = now - storedAt
    return {
      value: parsed.value,
      storedAt,
      age,
      stale: ttl > 0 && age > ttl,
      // Older than 10x TTL — probably safer to ignore than paint. Callers
      // that pass ttl=0 (never-expire) never hit this.
      expired: ttl > 0 && age > ttl * 10,
    }
  } catch { return null }
}

// Write an entry. ttl in milliseconds; 0 = never expires (rare — use for
// values that only refresh on explicit invalidation).
export function writeCache(key, value, ttl = 0) {
  try {
    localStorage.setItem(NS + key, JSON.stringify({
      value,
      storedAt: Date.now(),
      ttl,
    }))
  } catch { /* quota / private mode — silent, cache is best-effort */ }
}

// Drop an entry. Used after a definitive server truth (e.g. logout, or a
// hydrate that supersedes the cache).
export function clearCache(key) {
  try { localStorage.removeItem(NS + key) } catch { /* ignore */ }
}

// SWR primitive. Returns a getter that:
//   1. Reads cache immediately and returns the cached value (if any) via
//      `onCached` — the caller renders instantly.
//   2. Kicks off `fetcher()` in the background regardless of cache
//      freshness (SWR always revalidates). On success, writes the result
//      to cache and calls `onFresh`.
//   3. Silences fetch errors — the caller keeps whatever it painted from
//      cache. Optional `onError` for when the caller does want to react.
//
// The two-callback shape lets React components render cache on the first
// tick and merge fresh data on the second without threading a Promise
// through their state machinery.
export async function swr(key, ttl, fetcher, { onCached, onFresh, onError } = {}) {
  const cached = readCache(key)
  const painted = cached && !cached.expired ? cached.value : null
  if (painted && onCached) onCached(painted, cached)
  try {
    const fresh = await fetcher()
    if (fresh !== undefined) {
      writeCache(key, fresh, ttl)
      if (onFresh) onFresh(fresh)
    }
    return fresh
  } catch (err) {
    if (onError) onError(err)
    return painted
  }
}

// Common TTLs. Callers can pass raw ms too — these are just readability
// aids for the frequently-used ones.
export const TTL = {
  MINUTE: 60 * 1000,
  FIFTEEN_MIN: 15 * 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
}
